import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';

/* ============================================================
   VISUAL MOCKUP COMPONENTS — phone frames, cards, tables, etc.
   ============================================================ */

const MockPhone = ({ children, title }) => (
  <div style={{ background: '#1a1a2e', borderRadius: 22, padding: 8, maxWidth: 270, margin: '16px auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
    <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', minHeight: 180 }}>
      {title && (
        <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#0f3460)', color: '#fff', padding: '7px 14px', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>🛏️</span>{title}
          <span style={{ marginLeft: 'auto', position: 'relative' }}>🔔<span style={{ position: 'absolute', top: -4, right: -6, background: '#D13438', color: '#fff', fontSize: 7, width: 12, height: 12, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>3</span></span>
        </div>
      )}
      <div style={{ padding: 12, fontSize: 11 }}>{children}</div>
    </div>
  </div>
);

const MockCard = ({ title, children, color = '#fff', border }) => (
  <div style={{ background: color, borderRadius: 10, padding: '10px 12px', marginBottom: 8, border: border || '1px solid #edebe9', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
    {title && <div style={{ fontSize: 10, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>{title}</div>}
    {children}
  </div>
);

const MockBtn = ({ children, color = '#E86E1C', small }) => (
  <span style={{ display: 'inline-block', background: color, color: '#fff', borderRadius: 6, padding: small ? '2px 8px' : '4px 12px', fontSize: small ? 8 : 9, fontWeight: 700, margin: '2px 4px 2px 0' }}>{children}</span>
);

const MockBadge = ({ children, color = '#107C10' }) => (
  <span style={{ display: 'inline-block', background: `${color}18`, color, borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 600 }}>{children}</span>
);

const MockField = ({ label, value, ph }) => (
  <div style={{ marginBottom: 6 }}>
    <div style={{ fontSize: 8, fontWeight: 600, color: '#605e5c', marginBottom: 2 }}>{label}</div>
    <div style={{ background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 4, padding: '3px 6px', fontSize: 9, color: ph ? '#a19f9d' : '#323130' }}>{value}</div>
  </div>
);

const MockTable = ({ headers, rows }) => (
  <div style={{ border: '1px solid #edebe9', borderRadius: 6, overflow: 'hidden', marginTop: 4 }}>
    <div style={{ display: 'flex', background: '#f3f2f1', padding: '3px 0' }}>
      {headers.map((h, i) => <div key={i} style={{ flex: 1, fontSize: 7, fontWeight: 700, color: '#605e5c', padding: '0 4px' }}>{h}</div>)}
    </div>
    {rows.map((row, i) => (
      <div key={i} style={{ display: 'flex', padding: '3px 0', borderTop: '1px solid #f3f2f1', alignItems: 'center' }}>
        {row.map((cell, j) => <div key={j} style={{ flex: 1, fontSize: 8, color: '#323130', padding: '0 4px' }}>{cell}</div>)}
      </div>
    ))}
  </div>
);

const MockNotif = ({ icon, title, time, unread }) => (
  <div style={{ display: 'flex', gap: 6, padding: '6px 8px', background: unread ? '#F0F6FF' : '#fff', borderRadius: 6, marginBottom: 3, border: '1px solid #f3f2f1' }}>
    <span style={{ fontSize: 14 }}>{icon}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 8, fontWeight: unread ? 700 : 500 }}>{title}</div>
      <div style={{ fontSize: 7, color: '#a19f9d' }}>{time}</div>
    </div>
    {unread && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#0078D4', marginTop: 3 }} />}
  </div>
);

const Tip = ({ children }) => (
  <div style={{ display: 'flex', gap: 10, padding: '12px 16px', background: '#FFF8E1', borderRadius: 10, border: '1px solid #FFD54F', marginTop: 14, marginBottom: 8, fontSize: 13, lineHeight: 1.6, color: '#5D4037' }}>
    <span style={{ fontSize: 20, flexShrink: 0 }}>💡</span>
    <div>{children}</div>
  </div>
);

const Warning = ({ children }) => (
  <div style={{ display: 'flex', gap: 10, padding: '12px 16px', background: '#FDE7E9', borderRadius: 10, border: '1px solid #EF9A9A', marginTop: 14, marginBottom: 8, fontSize: 13, lineHeight: 1.6, color: '#B71C1C' }}>
    <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
    <div>{children}</div>
  </div>
);

const StepList = ({ steps }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '16px 0' }}>
    {steps.map((step, i) => (
      <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#E86E1C', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, flexShrink: 0 }}>{i + 1}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>{step.title}</div>
          <div style={{ fontSize: 13, color: '#605e5c', lineHeight: 1.6 }}>{step.desc}</div>
        </div>
      </div>
    ))}
  </div>
);

const FeatureGrid = ({ features }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, margin: '14px 0' }}>
    {features.map((f, i) => (
      <div key={i} style={{ background: '#faf9f8', borderRadius: 12, padding: 14, textAlign: 'center', border: '1px solid #edebe9' }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>{f.icon}</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>{f.title}</div>
        <div style={{ fontSize: 11, color: '#605e5c', lineHeight: 1.4 }}>{f.desc}</div>
      </div>
    ))}
  </div>
);


/* ============================================================
   SECTION CONTENT PER ROLE
   ============================================================ */

const getSections = (rol) => {
  const all = [];

  // ===== WELKOM =====
  all.push({
    key: 'welkom', icon: '👋', title: 'Welkom & installatie',
    roles: ['medewerker', 'manager', 'admin', 'flexkracht'],
    render: () => (
      <>
        <p style={S.text}>Welkom bij de <strong>Slaaploods Verlofbeheer</strong> app! Deze handleiding loodst je stap voor stap door alle functies — aangepast aan jouw profiel.</p>
        <FeatureGrid features={[
          { icon: '📱', title: 'PWA App', desc: 'Installeer als echte app op je telefoon' },
          { icon: '🔔', title: 'Meldingen', desc: 'Automatische push- en in-app meldingen' },
          { icon: '📅', title: 'Kalender', desc: 'Teamkalender met alle verlof' },
          { icon: '🔒', title: 'Privacy', desc: 'Collega\'s zien alleen labels, geen details' },
        ]} />

        <h4 style={S.subhead}>📱 App installeren op je telefoon</h4>
        <div style={S.twoCol}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#323130', marginBottom: 8 }}>iPhone (iOS)</div>
            <StepList steps={[
              { title: 'Open de link in Safari', desc: 'Tik op de link die je hebt ontvangen.' },
              { title: 'Tik op het deel-icoon ⎙', desc: 'Het vierkant met pijl omhoog, onderaan het scherm.' },
              { title: 'Kies "Zet op beginscherm"', desc: 'Scroll eventueel naar beneden in het menu.' },
              { title: 'Tik op "Voeg toe"', desc: 'De app verschijnt als tegel op je beginscherm.' },
            ]} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#323130', marginBottom: 8 }}>Android</div>
            <StepList steps={[
              { title: 'Open de link in Chrome', desc: 'Gebruik altijd Chrome voor de beste ervaring.' },
              { title: 'Tik op "Installeren"', desc: 'Of gebruik het menu ⋮ → "App installeren".' },
              { title: 'Klaar!', desc: 'De app staat nu op je beginscherm met meldingen.' },
            ]} />
          </div>
        </div>

        <Tip>Na installatie ontvang je <strong>meldingen met een getal op het app-icoon</strong> — net als bij WhatsApp of e-mail. Zo mis je nooit een goedkeuring of nieuw verzoek.</Tip>

        <MockPhone title="Inloggen">
          <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
            <div style={{ fontSize: 36, marginBottom: 2 }}>🛏️</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>Slaaploods Verlofbeheer</div>
          </div>
          <MockField label="E-mailadres" value="jouw@email.nl" />
          <MockField label="Wachtwoord" value="••••••••" />
          <div style={{ textAlign: 'center', margin: '8px 0' }}><MockBtn>Inloggen</MockBtn></div>
        </MockPhone>

        <h4 style={S.subhead}>🔑 Wachtwoord wijzigen</h4>
        <p style={S.text}>Je kunt je eigen wachtwoord wijzigen via het 🔑 icoon in de zijbalk (desktop) of via de "Meer" tab op mobiel. Je moet je huidige wachtwoord invoeren ter verificatie, daarna een nieuw wachtwoord kiezen en bevestigen. Eisen: minimaal 8 tekens, minstens 1 hoofdletter, 1 kleine letter en 1 cijfer.</p>
        <MockPhone title="Wachtwoord wijzigen">
          <MockField label="Huidig wachtwoord" value="••••••••" />
          <MockField label="Nieuw wachtwoord" value="••••••••" />
          <MockField label="Bevestig nieuw wachtwoord" value="••••••••" />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '8px 0' }}>
            <MockBtn variant="secondary">Annuleren</MockBtn>
            <MockBtn>✓ Wijzigen</MockBtn>
          </div>
        </MockPhone>
        <Tip>Wachtwoorden worden versleuteld opgeslagen (bcrypt hash). Zelfs de beheerder kan je wachtwoord niet inzien — alleen resetten naar een tijdelijk wachtwoord. Bij een reset word je direct gedwongen een nieuw wachtwoord te kiezen.</Tip>
      </>
    ),
  });

  // ===== DASHBOARD =====
  all.push({
    key: 'dashboard', icon: '📊', title: 'Dashboard',
    roles: ['medewerker', 'manager', 'admin'],
    render: () => (
      <>
        <p style={S.text}>Het Dashboard is je startpagina. Hier zie je in één oogopslag je saldi, recente aanvragen en openstaande acties.</p>

        <MockPhone title="Dashboard">
          <MockCard title="📊 Verlofsaldo 2026" color="#f0f6ff" border="1px solid #c7e0f4">
            <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', fontSize: 9, marginTop: 4 }}>
              <div><div style={{ fontSize: 16, fontWeight: 800, color: '#107C10' }}>152</div>Totaal</div>
              <div><div style={{ fontSize: 16, fontWeight: 800, color: '#E86E1C' }}>40</div>Opgenomen</div>
              <div><div style={{ fontSize: 16, fontWeight: 800, color: '#0078D4' }}>112</div>Resterend</div>
            </div>
          </MockCard>
          <MockCard title="⏰ Overuren">
            <div style={{ fontSize: 9, color: '#605e5c' }}>Verdiend: 24u · Gebruikt: 8u · <strong style={{ color: '#107C10' }}>Saldo: 16u</strong></div>
          </MockCard>
          <div style={{ fontSize: 9, fontWeight: 700, marginTop: 6, marginBottom: 2 }}>Recente aanvragen</div>
          <MockTable headers={['Type', 'Datum', 'Status']} rows={[
            ['Vakantie', '24 feb', <MockBadge>Goedgekeurd</MockBadge>],
            ['Bijz. verlof', '10 mrt', <MockBadge color="#FFB900">Ingediend</MockBadge>],
          ]} />
        </MockPhone>

        <h4 style={S.subhead}>Wat zie je op het Dashboard?</h4>
        <div style={S.twoCol}>
          <div>
            <ul style={S.list}>
              <li><strong>Verlofsaldo</strong> — Totaal (contract + meegenomen), opgenomen en resterend</li>
              <li><strong>Overuren saldo</strong> — Verdiend, gebruikt en resterend</li>
              <li><strong>Flex/ADV balans</strong> — Als flex actief is voor jouw account</li>
              <li><strong>Recente aanvragen</strong> — Je laatste verlof met status</li>
            </ul>
          </div>
          {(rol === 'admin' || rol === 'manager') && (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#E86E1C', marginBottom: 6 }}>Extra voor {rol === 'admin' ? 'beheerders' : 'managers'}:</div>
              <ul style={S.list}>
                <li><strong>Openstaande aanvragen</strong> — Direct ✅ goedkeuren of ❌ afkeuren</li>
                <li><strong>Snel verlof indienen</strong> — In één formulier voor een medewerker</li>
                <li><strong>Open ziekmeldingen</strong> — Altijd zichtbaar bovenaan</li>
              </ul>
            </div>
          )}
        </div>

        {(rol === 'admin' || rol === 'manager') && (
          <>
            <h4 style={S.subhead}>⚡ Snel verlof indienen (dashboard)</h4>
            <p style={S.text}>Onderaan het Dashboard staat een snelformulier waarmee je in één stap verlof indient namens een medewerker: kies medewerker → type → datum → uren → indienen. Vink <strong>✅ Direct goedkeuren</strong> aan om de aanvraag meteen goed te keuren zonder apart te hoeven beoordelen.</p>
            <MockPhone title="Snel indienen">
              <MockField label="Medewerker" value="Lisa de Vries" />
              <MockField label="Type verlof" value="✈️ Vakantie" />
              <div style={{ display: 'flex', gap: 6 }}>
                <div style={{ flex: 1 }}><MockField label="Start" value="24-02-2026" /></div>
                <div style={{ flex: 1 }}><MockField label="Eind" value="28-02-2026" /></div>
              </div>
              <MockField label="Uren" value="40" />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, fontSize: 9 }}>
                <span>☑️ Direct goedkeuren</span>
                <MockBtn color="#107C10">✅ Goedkeuren voor Lisa</MockBtn>
              </div>
            </MockPhone>
            <Tip>Uren accepteren ook komma-notatie (bijv. <strong>2,5</strong> voor 2½ uur). Met start-/eindtijd worden uren automatisch berekend op basis van het werkrooster.</Tip>
          </>
        )}
      </>
    ),
  });

  // ===== INDIENEN =====
  if (rol !== 'flexkracht') {
    all.push({
      key: 'indienen', icon: '📝', title: 'Indienen: verlof, ziek & overuren',
      roles: ['medewerker', 'manager', 'admin'],
      render: () => (
        <>
          <p style={S.text}>Via <strong>📝 Indienen</strong> in het menu kies je wat je wilt doen. Je komt op een keuzescherm:</p>

          <MockPhone title="Indienen">
            {[
              { icon: '✈️', name: 'Verlof aanvragen', desc: 'Vakantie, bijzonder verlof, flex', color: '#0078D4' },
              { icon: '🏥', name: 'Ziekmelding', desc: 'Jezelf of collega ziekmelden', color: '#D13438' },
              { icon: '⏰', name: 'Overuren registreren', desc: 'Werkdag of zaterdag', color: '#E86E1C' },
            ].map((item, i) => (
              <MockCard key={i} color={`${item.color}08`} border={`2px solid ${item.color}40`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 22 }}>{item.icon}</span>
                  <div><div style={{ fontSize: 11, fontWeight: 700, color: item.color }}>{item.name}</div><div style={{ fontSize: 8, color: '#605e5c' }}>{item.desc}</div></div>
                </div>
              </MockCard>
            ))}
          </MockPhone>

          <h4 style={S.subhead}>✈️ Verlof aanvragen — stap voor stap</h4>
          <StepList steps={[
            { title: 'Kies type verlof', desc: 'Selecteer uit het dropdown-menu: vakantie, bijzonder verlof, flex (plus of min), of een ander type. Flex (plus) = extra gewerkt, Flex (min) = flex-verlof opnemen.' },
            { title: 'Vul start- en einddatum in', desc: 'De uren worden automatisch berekend op basis van je werkrooster. Feestdagen en weekenden worden overgeslagen. Uren accepteren ook komma-notatie (bijv. 2,5 voor 2½ uur).' },
            { title: 'Optioneel: begin-/eindtijd', desc: 'Kies "Met begin/eindtijd" als je een halve dag of specifieke uren wilt invullen. Begin- en eindtijd zijn optioneel: laat je ze leeg, dan wordt de hele werkdag volgens je werkrooster berekend.' },
            { title: 'Optioneel: overuren inzetten', desc: 'Heb je overuren? Vink "Overuren gebruiken" aan om je verlof-saldo te sparen.' },
            { title: 'Verstuur', desc: 'Klik "Aanvraag indienen". Je manager/beheerder krijgt direct een in-app melding en een badge op hun app-icoon.' },
          ]} />

          <MockPhone title="Verlof aanvragen">
            <MockField label="Type verlof" value="✈️ Vakantie" />
            <div style={{ display: 'flex', gap: 6 }}>
              <div style={{ flex: 1 }}><MockField label="Startdatum" value="24-02-2026" /></div>
              <div style={{ flex: 1 }}><MockField label="Einddatum" value="28-02-2026" /></div>
            </div>
            <MockCard color="#dff6dd" border="1px solid #a7e3a5">
              <div style={{ fontSize: 9, fontWeight: 700, color: '#107C10' }}>⏱️ Berekend: 40 uur (5 werkdagen)</div>
              <div style={{ fontSize: 8, color: '#605e5c', marginTop: 2 }}>Ma 8,5u · Di 8,5u · Wo 4,5u · Do 8,5u · Vr 10u</div>
            </MockCard>
            <MockField label="Opmerkingen (optioneel)" value="Voorjaarsvakantie" />
            <div style={{ textAlign: 'center', marginTop: 6 }}><MockBtn>Aanvraag indienen</MockBtn></div>
          </MockPhone>

          {(rol === 'admin' || rol === 'manager') && (
            <Tip>Als manager of beheerder kun je bovenaan het formulier een <strong>medewerker selecteren</strong> om verlof namens hen in te dienen. Vink <strong>✅ Direct goedkeuren</strong> aan om de aanvraag meteen goed te keuren. Jouw naam wordt vastgelegd bij de aanvraag.</Tip>
          )}

          <h4 style={S.subhead}>🏥 Ziekmelding</h4>
          <p style={S.text}>Ziekmeldingen worden <strong>direct goedgekeurd</strong> — geen wachttijd. Een ziekmelding begint standaard als "open" (zonder einddatum). Later doe je een betermelding wanneer de medewerker weer aan het werk is.</p>
          <StepList steps={[
            { title: 'Kies startdatum', desc: 'Standaard vandaag. Optioneel met starttijd voor een deel van de dag.' },
            { title: 'Laat "Open ziekmelding" aan', desc: 'De medewerker is ziek tot nader order. Geen einddatum nodig.' },
            { title: 'Betermelden', desc: 'Ga later naar Verlofoverzicht → klik op 💪 bij de open ziekmelding → vul einddatum in.' },
          ]} />

          <h4 style={S.subhead}>⏰ Overuren registreren</h4>
          <StepList steps={[
            { title: 'Kies datum', desc: 'Het systeem herkent automatisch werkdag vs. zaterdag.' },
            { title: 'Vul start- en eindtijd', desc: 'Werkdagen: pauze 12:30–13:00 wordt automatisch afgetrokken. Zaterdagen: geen pauze-aftrek.' },
            { title: 'Wacht op goedkeuring', desc: 'Overuren moeten door de beheerder worden goedgekeurd. Je krijgt een melding bij goed-/afkeuring.' },
          ]} />
          <Tip>Goedgekeurde overuren kun je later <strong>inzetten bij verlofaanvragen</strong> om je vakantie-uren te besparen. Vink "Overuren gebruiken" aan in het verlofformulier.</Tip>
        </>
      ),
    });
  }

  // ===== VERLOFOVERZICHT =====
  if (rol !== 'flexkracht') {
    all.push({
      key: 'overzicht', icon: '📋', title: 'Verlofoverzicht',
      roles: ['medewerker', 'manager', 'admin'],
      render: () => (
        <>
          <p style={S.text}>{(rol === 'admin' || rol === 'manager') ? 'Het verlofoverzicht toont alle verlofaanvragen van het team. Je kunt hier aanvragen goedkeuren, afkeuren, wijzigen en betermelden.' : 'Het verlofoverzicht toont al je eigen verlofaanvragen met status.'}</p>

          <MockPhone title="Verlofoverzicht">
            <div style={{ display: 'flex', gap: 3, marginBottom: 6 }}>
              {['Alle', 'Ingediend', 'Goedgekeurd', 'Afgekeurd'].map((f, i) => (
                <MockBtn key={f} color={i === 0 ? '#E86E1C' : '#a19f9d'} small>{f}</MockBtn>
              ))}
            </div>
            <MockTable headers={rol === 'medewerker' ? ['Type', 'Periode', 'Uren', 'Status'] : ['Wie', 'Type', 'Periode', 'Status', 'Actie']} rows={rol === 'medewerker' ? [
              ['Vakantie', '24–28 feb', '40u', <MockBadge>Goedgekeurd</MockBadge>],
              ['BV', '10 mrt', '8u', <MockBadge color="#FFB900">Ingediend</MockBadge>],
            ] : [
              ['Lisa', 'Vakantie', '24–28 feb', <MockBadge color="#FFB900">Ingediend</MockBadge>, <span style={{ fontSize: 10 }}>✅ ❌</span>],
              ['Pieter', 'Ziek', '20 feb →', <MockBadge color="#D13438">Open ziek</MockBadge>, <span style={{ fontSize: 10 }}>💪</span>],
              ['Erwin', 'BV', '15 feb', <MockBadge>Goedgekeurd</MockBadge>, <span style={{ fontSize: 10 }}>✏️</span>],
            ]} />
          </MockPhone>

          <h4 style={S.subhead}>Status filters</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {[
              { label: 'Ingediend', color: '#FFB900', desc: 'Wacht op beoordeling' },
              { label: 'Goedgekeurd', color: '#107C10', desc: 'Verlof is toegekend' },
              { label: 'Afgekeurd', color: '#D13438', desc: 'Verlof is afgewezen' },
              { label: 'Ingetrokken', color: '#767676', desc: 'Door medewerker ingetrokken' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: `${s.color}10`, padding: '4px 12px', borderRadius: 8, border: `1px solid ${s.color}30` }}>
                <MockBadge color={s.color}>{s.label}</MockBadge>
                <span style={{ fontSize: 11, color: '#605e5c' }}>{s.desc}</span>
              </div>
            ))}
          </div>

          <h4 style={S.subhead}>Beschikbare acties</h4>
          <MockTable headers={['Knop', 'Actie', 'Beschikbaar voor']} rows={[
            ['✅', 'Goedkeuren', 'Manager / Beheerder'],
            ['❌', 'Afkeuren', 'Manager / Beheerder'],
            ['✏️', 'Wijzigen (datum, uren, type)', 'Eigen aanvragen + Beheerder'],
            ['💪', 'Betermelden (open ziekmelding)', 'Beheerder / Medewerker zelf'],
            ['↩️', 'Intrekken', 'Eigen aanvragen (indien ingediend)'],
          ]} />

          {(rol === 'admin' || rol === 'manager') && (
            <Tip>Bij het goed- of afkeuren krijgt de medewerker <strong>automatisch een melding</strong> in de app, inclusief badge-getal op het app-icoon.</Tip>
          )}
          {rol === 'manager' && (
            <Warning><strong>Manager beperking:</strong> Je ziet opmerkingen alleen als je de aanvraag zelf hebt ingediend. Aanvragen ouder dan 1 maand worden verborgen (behalve openstaande en open ziekmeldingen).</Warning>
          )}
        </>
      ),
    });
  }

  // ===== KALENDER =====
  all.push({
    key: 'kalender', icon: '📅', title: 'Kalender',
    roles: ['medewerker', 'manager', 'admin', 'flexkracht'],
    render: () => (
      <>
        <p style={S.text}>De teamkalender toont alle goedgekeurde verlof, ziekmeldingen, feestdagen, vakantieperioden en roosterwijzigingen.</p>

        <MockPhone title="Kalender — Februari 2026">
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 6 }}>
            <MockBtn small>Maand</MockBtn>
            <MockBtn color="#605e5c" small>Week</MockBtn>
            <MockBtn color="#605e5c" small>Dag</MockBtn>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, fontSize: 7, textAlign: 'center' }}>
            {['Ma','Di','Wo','Do','Vr','Za','Zo'].map(d => <div key={d} style={{ fontWeight: 700, color: '#605e5c', padding: 2 }}>{d}</div>)}
            {Array.from({ length: 28 }, (_, i) => {
              const isWeekend = [5,6,12,13,19,20,26,27].includes(i);
              const isVakantie = i >= 22 && i <= 26;
              return (
                <div key={i} style={{ padding: 2, background: isVakantie ? '#107C1015' : isWeekend ? '#faf9f8' : '#fff', borderRadius: 3, minHeight: 22 }}>
                  <div style={{ fontWeight: 500, color: isWeekend ? '#a19f9d' : '#323130' }}>{i + 1}</div>
                  {i === 3 && <div style={{ background: '#D13438', color: '#fff', borderRadius: 2, fontSize: 5, padding: '0 1px', marginTop: 1 }}>Z Pieter</div>}
                  {isVakantie && <div style={{ background: '#107C10', color: '#fff', borderRadius: 2, fontSize: 5, padding: '0 1px', marginTop: 1 }}>V Lisa</div>}
                  {i === 14 && <div style={{ background: '#0078D4', color: '#fff', borderRadius: 2, fontSize: 5, padding: '0 1px', marginTop: 1 }}>BV Erwin</div>}
                </div>
              );
            })}
          </div>
        </MockPhone>

        <h4 style={S.subhead}>Labels in de kalender</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
          {[
            { label: 'V', color: '#107C10', text: 'Vakantie' },
            { label: 'Z', color: '#D13438', text: 'Ziek' },
            { label: 'BV', color: '#0078D4', text: 'Bijzonder verlof' },
            { label: 'F', color: '#9B59B6', text: 'Flex/ADV' },
            { label: '🏖️', color: '#FFB900', text: 'Vakantieperiode' },
            { label: '🎄', color: '#E86E1C', text: 'Feestdag' },
          ].map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#faf9f8', borderRadius: 6, padding: '4px 10px', border: '1px solid #edebe9' }}>
              <span style={{ background: l.color, color: '#fff', borderRadius: 3, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>{l.label}</span>
              <span style={{ fontSize: 12, color: '#323130' }}>{l.text}</span>
            </div>
          ))}
        </div>

        <h4 style={S.subhead}>Drie weergaven</h4>
        <FeatureGrid features={[
          { icon: '📆', title: 'Maand', desc: 'Overzicht met gekleurde blokken per medewerker per dag' },
          { icon: '📅', title: 'Week', desc: 'Gantt-view met tijden en verlof per medewerker — schuif horizontaal voor meer dagen' },
          { icon: '📌', title: 'Dag', desc: 'Alle details: wie is er, wie niet, welk type verlof' },
        ]} />

        <Tip><strong>Week-weergave:</strong> Swipe horizontaal over de tijdlijn om alle dagen van de week te zien. De medewerkernamen blijven links 'plakken' (sticky) zodat je weet van wie de rij is.</Tip>

        <Tip><strong>Privacy:</strong> Collega's zien bij jouw verlof alleen het korte label (bijv. "V" of "BV"). Alleen jijzelf en de beheerder zien het volledige type en de reden.</Tip>
      </>
    ),
  });

  // ===== MELDINGEN =====
  all.push({
    key: 'meldingen', icon: '🔔', title: 'Meldingen & notificaties',
    roles: ['medewerker', 'manager', 'admin', 'flexkracht'],
    render: () => (
      <>
        <p style={S.text}>Je ontvangt <strong>automatisch meldingen</strong> bij alle belangrijke gebeurtenissen. Het rode getal op de 🔔 bel in de header toont het aantal ongelezen meldingen.</p>

        <MockPhone title="Meldingen">
          <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #edebe9', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderBottom: '1px solid #f3f2f1', background: '#faf9f8' }}>
              <span style={{ fontSize: 10, fontWeight: 700 }}>🔔 Meldingen <span style={{ color: '#D13438' }}>(3)</span></span>
              <span style={{ fontSize: 8, color: '#0078D4', fontWeight: 600, cursor: 'pointer' }}>Alles gelezen</span>
            </div>
            <MockNotif icon="✅" title="Je verlofaanvraag is goedgekeurd (24–28 feb)" time="5 min geleden" unread />
            <MockNotif icon="✈️" title="Nieuwe verlofaanvraag: Lisa (vakantie)" time="1u geleden" unread />
            <MockNotif icon="🏥" title="Ziekmelding ingediend: Pieter" time="gisteren" unread />
            <MockNotif icon="⏰" title="Overuren goedgekeurd: 4,5 uur" time="2d geleden" />
            <MockNotif icon="⚡" title="Je bent ingepland: za 28 feb" time="3d geleden" />
          </div>
        </MockPhone>

        <h4 style={S.subhead}>Wanneer krijg je een melding?</h4>
        <MockTable headers={['Gebeurtenis', 'Wie ontvangt melding']} rows={[
          ['✈️ Verlof aangevraagd', 'Beheerders & managers'],
          ['✈️ Verlof namens medewerker ingediend', 'De betreffende medewerker'],
          ['✅ Verlof goedgekeurd', 'De medewerker'],
          ['❌ Verlof afgekeurd', 'De medewerker'],
          ['↩️ Verlof ingetrokken', 'De medewerker'],
          ['🏥 Ziekmelding', 'Beheerders & managers'],
          ['💪 Betermelding', 'Beheerders & managers'],
          ['⏰ Overuren geregistreerd', 'Beheerders'],
          ['✅ Overuren goedgekeurd/afgekeurd', 'De medewerker'],
          ['⚡ Flexkracht ingepland', 'De flexkracht'],
        ]} />

        <h4 style={S.subhead}>Badge op je telefoon</h4>
        <p style={S.text}>Als de app is geïnstalleerd verschijnt het aantal ongelezen meldingen als een <strong>getal op het app-icoon</strong>:</p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 32, margin: '20px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', background: '#1a1a2e', borderRadius: 16, width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <span style={{ fontSize: 26 }}>🛏️</span>
              <div style={{ position: 'absolute', top: -4, right: -4, background: '#D13438', color: '#fff', width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, border: '2px solid #fff' }}>3</div>
            </div>
            <div style={{ fontSize: 10, color: '#605e5c', marginTop: 6 }}>3 ongelezen</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', background: '#1a1a2e', borderRadius: 16, width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <span style={{ fontSize: 26 }}>🛏️</span>
            </div>
            <div style={{ fontSize: 10, color: '#a19f9d', marginTop: 6 }}>Alles gelezen ✓</div>
          </div>
        </div>

        <h4 style={S.subhead}>Hoe werkt het?</h4>
        <ul style={S.list}>
          <li>Tik op <strong>🔔</strong> in de header om je meldingen te bekijken</li>
          <li>Tik op een melding om naar de betreffende pagina te gaan</li>
          <li>Gelezen meldingen worden lichter weergegeven</li>
          <li>Klik <strong>"Alles gelezen"</strong> om alle meldingen als gelezen te markeren</li>
          <li>Oude meldingen worden na 30 dagen automatisch opgeruimd</li>
        </ul>
      </>
    ),
  });

  // ===== FLEX =====
  if (rol === 'flexkracht') {
    all.push({
      key: 'flex', icon: '⚡', title: 'Mijn Flex Dashboard',
      roles: ['flexkracht'],
      render: () => (
        <>
          <p style={S.text}>Als flexkracht heb je een eigen dashboard met je planning, uren en saldi.</p>
          <MockPhone title="Mijn Flex">
            <MockCard title="📊 Mijn uren 2026" color="#f0f6ff" border="1px solid #c7e0f4">
              <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', fontSize: 9, marginTop: 4 }}>
                <div><div style={{ fontSize: 14, fontWeight: 800 }}>48</div>Ingepland</div>
                <div><div style={{ fontSize: 14, fontWeight: 800, color: '#107C10' }}>32</div>Gewerkt</div>
                <div><div style={{ fontSize: 14, fontWeight: 800, color: '#E86E1C' }}>24</div>Uitbetaald</div>
              </div>
            </MockCard>
            <div style={{ fontSize: 9, fontWeight: 700, marginTop: 6, marginBottom: 2 }}>Komende diensten</div>
            <MockTable headers={['Datum', 'Tijd', 'Uren', 'Opm.']} rows={[
              ['Za 28 feb', '09:00–15:00', '6u', 'Magazijn'],
              ['Za 7 mrt', '08:00–14:00', '6u', '—'],
            ]} />
          </MockPhone>

          <h4 style={S.subhead}>Uren invoeren</h4>
          <StepList steps={[
            { title: 'Ga naar het tabblad "Uren invoeren"', desc: 'Vul datum, starttijd en eindtijd in. De uren worden automatisch berekend.' },
            { title: 'Inklokken', desc: 'Je kunt ook alleen inklokken bij aanvang — de eindtijd vul je in als je klaar bent (uitklokken).' },
            { title: 'Overzicht', desc: 'Bekijk per jaar je totaal ingeplande, gewerkte en uitbetaalde uren.' },
          ]} />
          <Tip>Je ontvangt een <strong>melding</strong> zodra je ingepland wordt voor een nieuwe dienst.</Tip>
        </>
      ),
    });
  }

  if (rol === 'admin' || rol === 'manager') {
    all.push({
      key: 'flex', icon: '⚡', title: 'Flexkrachten inplannen',
      roles: ['manager', 'admin'],
      render: () => (
        <>
          <p style={S.text}>Via <strong>⚡ Flexkrachten</strong> in het menu plan je flexkrachten in en beheer je hun uren.</p>
          <StepList steps={[
            { title: 'Inplannen', desc: 'Kies flexkracht, datum, start-/eindtijd en eventueel een omschrijving. De flexkracht krijgt automatisch een in-app melding.' },
            { title: 'Planning wijzigen/verwijderen', desc: 'In het overzicht kun je inplanningen aanpassen of verwijderen met ✏️ en 🗑️.' },
            { title: 'Gewerkte uren bekijken', desc: 'Flexkrachten voeren zelf hun uren in. Je ziet deze in het tabblad "Uren".' },
          ]} />
          {rol === 'admin' && (
            <>
              <h4 style={S.subhead}>Uitbetalen & saldocorrecties</h4>
              <p style={S.text}>Via <strong>Beheer → Flex beheer</strong> kun je gewerkte uren uitbetalen en eventuele saldocorrecties doorvoeren.</p>

              <h4 style={S.subhead}>Flex/ADV regels (automatisch afgedwongen)</h4>
              <ul style={S.list}>
                <li><strong>Weekbudget:</strong> Stel per medewerker het wekelijks flex-budget in via het medewerker-formulier (veld "Week Flex/ADV uren")</li>
                <li><strong>Max 8u flex-min per week:</strong> Medewerkers kunnen maximaal 8 uur flex-min per kalenderweek opnemen</li>
                <li><strong>Maandbalans:</strong> Binnen een kalendermaand moeten plus- en minuren in balans zijn. Het systeem blokkeert het opsparen van alle plus-uren om ze in één week op te nemen</li>
                <li><strong>Max flex-min per maand:</strong> Het totaal flex-min per maand mag niet meer zijn dan weekFlexUren × 4</li>
              </ul>
            </>
          )}
        </>
      ),
    });
  }

  // ===== MEDEWERKER PORTAAL (admin) =====
  if (rol === 'admin') {
    all.push({
      key: 'portaal', icon: '👤', title: 'Medewerker portaal',
      roles: ['admin'],
      render: () => (
        <>
          <p style={S.text}>Via <strong>👤 Verlof medewerkers</strong> in het menu beheer je verlof per medewerker. Selecteer een medewerker en je ziet hun volledige saldo, verlofhistorie en kunt direct verlof indienen.</p>
          <MockPhone title="Verlof medewerkers">
            <MockField label="Selecteer medewerker" value="Lisa de Vries" />
            <MockCard title="📊 Saldo Lisa — 2026" color="#f0f6ff" border="1px solid #c7e0f4">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 8, marginTop: 4 }}>
                <div>Berekend: <strong>152u</strong></div>
                <div>Meegenomen: <strong>12u</strong></div>
                <div>Opgenomen: <strong>40u</strong></div>
                <div>Resterend: <strong style={{ color: '#107C10' }}>124u</strong></div>
                <div>Overuren: <strong>16u</strong></div>
                <div>Flex: <strong>+4u</strong></div>
              </div>
            </MockCard>
            <div style={{ fontSize: 9, fontWeight: 700, marginBottom: 2 }}>Verlofhistorie</div>
            <MockTable headers={['Type', 'Periode', 'Uren', 'Status']} rows={[
              ['Vakantie', '24–28 feb', '40u', <MockBadge>Goedgekeurd</MockBadge>],
            ]} />
          </MockPhone>
        </>
      ),
    });
  }

  // ===== BEHEER =====
  if (rol === 'admin') {
    all.push({
      key: 'beheer', icon: '⚙️', title: 'Beheer (instellingen)',
      roles: ['admin'],
      render: () => (
        <>
          <p style={S.text}>Via <strong>⚙️ Beheer</strong> heb je toegang tot alle administratie en instellingen van de app.</p>
          <FeatureGrid features={[
            { icon: '👥', title: 'Medewerkers', desc: 'Accounts aanmaken, wijzigen, werkroosters, wachtwoorden' },
            { icon: '📲', title: 'WhatsApp uitnodiging', desc: 'Stuur inloggegevens incl. tijdelijk wachtwoord via WhatsApp' },
            { icon: '📊', title: 'Overzicht', desc: 'Verlofsaldi, overuren, flex/ADV en ziekte per medewerker' },
            { icon: '🎄', title: 'Feestdagen', desc: 'Nationale feestdagen toevoegen en verwijderen' },
            { icon: '🏖️', title: 'Vakantieperioden', desc: 'Schoolvakanties instellen (waarschuwing bij aanvraag)' },
            { icon: '🔀', title: 'Roosterwijzigingen', desc: 'Incidentele werkdag-verschuivingen vastleggen' },
            { icon: '📋', title: 'Verloftypes', desc: 'Eigen types aanmaken met labels, iconen en saldo-aftrek' },
            { icon: '📅', title: 'Outlook Sync', desc: 'Synchronisatie met "Personeel- Verlofplanning" agenda' },
            { icon: '📦', title: 'Import & Export', desc: 'CSV import, JSON backup, PDF exports' },
            { icon: '💾', title: 'Backup & Restore', desc: 'Volledige backup downloaden en herstellen' },
            { icon: '🔒', title: 'Beveiliging', desc: 'Wachtwoord hashing, API keys overzicht' },
            { icon: '📂', title: 'Jaarafsluiting', desc: 'Saldi archiveren, uren meenemen naar nieuw jaar' },
            { icon: '🍔', title: 'Menu aanpassen', desc: 'Menu-items hernoemen en volgorde wijzigen' },
          ]} />

          <h4 style={S.subhead}>👥 Medewerkers beheren</h4>
          <StepList steps={[
            { title: 'Toevoegen', desc: 'Klik "+ Nieuwe medewerker". Vul naam, e-mail, wachtwoord, contracturen, afdeling en werkrooster in.' },
            { title: 'Verkorte naam', desc: 'Stel optioneel een korte naam in (bijv. "Erwin") voor Outlook agenda weergave. Zonder verkorte naam wordt de volledige naam gebruikt.' },
            { title: 'Werkrooster instellen', desc: 'Per dag: ochtend/middag/avond met start- en eindtijd. Dit bepaalt automatisch de urenberekening bij verlofaanvragen.' },
            { title: 'Profielen toekennen', desc: 'Geef extra profielen: Manager, Beheerder, Flexkracht, 🏪 Showroom. Meerdere profielen mogelijk. Alle toegekende profielen verschijnen als badges in de Profielen-kolom van het medewerker overzicht, inclusief Flex/ADV uren (bijv. "ADV 4u/w").' },
            { title: 'Flex/ADV aanzetten', desc: 'Vink "🔀 Flex/ADV" aan en stel het wekelijks flex-budget in (bijv. 4u/week). De medewerker kan dan Flex (plus) en Flex (min) boeken in het verlofformulier.' },
            { title: 'WhatsApp uitnodiging', desc: 'Klik op 📲 bij de medewerker voor een uitnodigingsbericht. Via de 🔑 knop kun je eerst een tijdelijk wachtwoord instellen en daarna direct meesturen in één WhatsApp bericht.' },
            { title: 'Status wijzigen', desc: 'Actief → Inactief (tijdelijk) of Uit dienst (permanent, wordt uitgelogd).' },
          ]} />

          <h4 style={S.subhead}>📊 Verlofsaldi overzicht</h4>
          <p style={S.text}>Het tabblad <strong>Overzicht</strong> in Beheer toont een compleet saldi-overzicht per medewerker met de volgende kolommen:</p>
          <MockPhone title="Beheer — Overzicht">
            <MockTable headers={['', 'Verlof', 'Overuren', 'Flex', 'Ziekte']} rows={[
              ['Erwin', '112u rest', '+16u saldo', 'Aan (4u/w)', '—'],
              ['Lisa', '80u rest', '⏳ 8u open', 'Uit', '🏥 24u'],
            ]} />
          </MockPhone>
          <ul style={S.list}>
            <li><strong>Verlofuren</strong> — Contract + meegenomen, opgenomen, resterend (pro-rata als in dienst na 1 jan)</li>
            <li><strong>Open overuren</strong> — Ingediende overuren die nog goedgekeurd moeten worden</li>
            <li><strong>Overuren saldo</strong> — Goedgekeurde overuren minus al ingezet voor verlof</li>
            <li><strong>Flex/ADV</strong> — Aan/Uit met weekuren configuratie + lopend flex saldo (plus - min)</li>
            <li><strong>Ziekte</strong> — Bij open ziekmelding: actuele uren berekend tot vandaag op basis van werkrooster. Toont ook eerder afgesloten ziekte-uren dit jaar</li>
          </ul>

          <h4 style={S.subhead}>📲 WhatsApp uitnodiging</h4>
          <p style={S.text}>Bij elke actieve medewerker staat een 📲 knop. Hiermee open je WhatsApp met een vooraf ingevuld bericht met de app-link, het e-mailadres en installatie-instructies voor iPhone en Android.</p>
          <p style={S.text}>Als er een <strong>telefoonnummer</strong> is ingevuld, wordt het bericht direct naar dat contact gestuurd. Zonder telefoonnummer opent WhatsApp met het bericht zodat je zelf het juiste contact kunt kiezen.</p>
          <Tip>De 📲 knop vraagt altijd om het wachtwoord voordat het bericht wordt verstuurd. Heb je het wachtwoord nog niet ingesteld? Gebruik eerst de 🔑 knop — daarna kun je via 📲 het bericht inclusief wachtwoord versturen.</Tip>

          <h4 style={S.subhead}>🔑 Wachtwoord instellen / resetten (admin)</h4>
          <p style={S.text}>Bij elke actieve medewerker staat een 🔑 knop waarmee je een <strong>tijdelijk wachtwoord</strong> kunt instellen. Na het opslaan verschijnt er een groene "📲 WhatsApp met wachtwoord" knop — hiermee stuur je de inloggegevens inclusief het tijdelijke wachtwoord direct via WhatsApp naar de medewerker.</p>
          <p style={S.text}>Na het instellen van een wachtwoord door de beheerder geldt automatisch:</p>
          <FeatureGrid features={[
            { icon: '🔐', title: 'Eerste login', desc: 'De medewerker ziet direct een scherm "Kies een nieuw wachtwoord" — de app is pas bruikbaar nadat dit is ingesteld.' },
            { icon: '🔄', title: 'Na elke reset', desc: 'Elke keer dat de beheerder het wachtwoord reset, wordt de medewerker opnieuw gedwongen een eigen wachtwoord te kiezen.' },
            { icon: '🔒', title: 'Niet te omzeilen', desc: 'Het scherm kan niet worden gesloten of overgeslagen. Alleen "Uitloggen" is mogelijk.' },
            { icon: '✅', title: 'Eenmalig', desc: 'Zodra de medewerker een eigen wachtwoord heeft ingesteld, verdwijnt de melding en is de app volledig bruikbaar.' },
          ]} />

          <h4 style={S.subhead}>📋 Werkrooster kopiëren</h4>
          <p style={S.text}>Bij het werkrooster staat naast "✏️ Bewerken" een dropdown "📋 Kopieer van...". Selecteer een andere medewerker om diens werkrooster over te nemen. Handig voor nieuwe medewerkers die hetzelfde schema werken als een bestaande collega.</p>

          <h4 style={S.subhead}>👁️ Inloggen als medewerker (impersonatie)</h4>
          <p style={S.text}>Onderaan de sidebar staat een dropdown om als een andere medewerker in te loggen. Je ziet precies wat zij zien — inclusief hun dashboard, saldo en kalender. Een <strong>gele banner</strong> bovenaan geeft aan dat je een ander profiel bekijkt. Alle acties worden gelogd met jouw naam.</p>

          <h4 style={S.subhead}>📅 Outlook agenda synchronisatie</h4>
          <p style={S.text}>De app synchroniseert verlofaanvragen naar de Outlook agenda <strong>"Personeel- Verlofplanning"</strong> van het gekoppelde account (bijv. willem@slaaploods.nl). Medewerkers kunnen deze agenda abonneren in hun eigen Outlook om het team-verlof te zien.</p>
          <StepList steps={[
            { title: 'Azure configuratie', desc: 'Vul de Client ID en Tenant ID in vanuit je Azure App Registration. Dit hoeft maar één keer.' },
            { title: 'Verbinden', desc: 'Klik "Verbinden met Outlook" en log in. De app zoekt automatisch de agenda "Personeel- Verlofplanning" op, of maakt deze aan.' },
            { title: 'Automatische sync', desc: 'Bij verlof/ziekmelding/betermelding wordt Outlook automatisch bijgewerkt (8 weken vooruit).' },
            { title: 'Handmatige sync', desc: 'Klik "📅 Synchroniseren" voor een volledige sync t/m 31 december volgend jaar.' },
            { title: 'Wis & sync', desc: 'Klik "🗑️ Alles wissen & opnieuw syncen" om alle events te verwijderen en opnieuw aan te maken.' },
          ]} />

          <h4 style={S.subhead}>📅 Werkrooster → Outlook</h4>
          <p style={S.text}>Per medewerker per werkdag verschijnt één event in Outlook. De <strong>verkorte naam</strong> (in te stellen bij Medewerkers → Bewerken) wordt gebruikt als weergavenaam. Outlook events tonen een korte afkorting + naam:</p>
          <FeatureGrid features={[
            { icon: '🟢', title: 'Beschikbaar', desc: '"Erwin 08:30-17:00" — reguliere werkdag' },
            { icon: '🔴', title: 'Vakantie', desc: '"VAK - Erwin" — hele dag verlof' },
            { icon: '🟡', title: 'Dagdeel', desc: '"VAK - Erwin (ochtend)" — gedeeltelijk verlof' },
            { icon: '🏥', title: 'Ziek', desc: '"ZIEK - Erwin" — oranje categorie' },
            { icon: '🔵', title: 'Flex/ADV', desc: '"ADV - Erwin" of "FLEX - Erwin"' },
            { icon: '⚠️', title: 'Onderbezetting', desc: 'Automatische waarschuwing bij te weinig personeel' },
          ]} />
          <p style={S.text}><strong>Automatische sync:</strong> De Outlook werkrooster-agenda wordt automatisch bijgewerkt (8 weken vooruit) bij de volgende acties:</p>
          <FeatureGrid features={[
            { icon: '💾', title: 'Werkrooster opslaan', desc: 'Na opslaan of kopiëren van een rooster' },
            { icon: '📋', title: 'Contractwijziging', desc: 'Na doorvoeren van een contractwijziging' },
            { icon: '🏥', title: 'Ziekmelding', desc: 'Zieke medewerker wordt direct oranje in Outlook' },
            { icon: '✅', title: 'Verlof goedkeuren', desc: 'Goedgekeurd verlof wordt verwerkt in werkrooster' },
            { icon: '💚', title: 'Betermelding', desc: 'Herstelde medewerker wordt weer groen in Outlook' },
          ]} />
          <Tip>Handmatige sync via "📅 Synchroniseren" synct t/m eind volgend jaar. Verkorte naam instellen bij Medewerkers → Bewerken voor kortere Outlook weergave (bijv. "Erwin" i.p.v. "Erwin Roks").</Tip>

          <h4 style={S.subhead}>🩺 Systeem Diagnostiek</h4>
          <p style={S.text}>In het tabblad <strong>Diagnose</strong> voer je een uitgebreide systeemcheck uit met 30+ automatische controles:</p>
          <FeatureGrid features={[
            { icon: '🗃️', title: 'Data integriteit', desc: 'Weesverlof, 0-uren, duplicaten, rooster ≠ contract' },
            { icon: '⚙️', title: 'Configuratie', desc: 'Feestdagen, Outlook, verloftypen, vakantieperiodes' },
            { icon: '📊', title: 'Saldi', desc: 'Negatieve saldi, ontbrekende contracturen, hoog verbruik' },
            { icon: '🔒', title: 'Beveiliging', desc: 'Ongehashte wachtwoorden, admin accounts, ontbrekende toegang' },
            { icon: '👥', title: 'Beschikbaarheid', desc: 'Bezetting, showroom-capaciteit, boekingssysteem gereedheid' },
            { icon: '🚀', title: 'Tools & integraties', desc: 'PWA status, Outlook sync, boekingssysteem readiness' },
          ]} />
          <p style={S.text}><strong>Auto-fix:</strong> Bij sommige problemen (ontbrekende feestdagen, ongehashte wachtwoorden, oude notificaties) kun je direct op een groene knop klikken om het automatisch op te lossen.</p>
          <p style={S.text}><strong>🤖 AI Analyse:</strong> Kopieer de diagnoseresultaten naar Claude of een andere AI-assistent voor uitgebreid advies met de nieuwste tools en best practices voor jouw situatie.</p>
          <p style={S.text}><strong>📋 Rapport:</strong> Exporteer de volledige diagnose als Markdown-bestand (.md) voor archivering of bespreking.</p>
          <Tip>Voer de check regelmatig uit (bijv. maandelijks). Elk probleem bevat severity (🔴 fout, 🟡 waarschuwing, 🔵 suggestie), een concrete aanpak, en een link naar het juiste tabblad.</Tip>

          <h4 style={S.subhead}>👥 Beschikbaarheid & Boekingssysteem</h4>
          <p style={S.text}>In het tabblad <strong>Beschikbaarheid</strong> zie je een real-time overzicht van personeelsbezetting:</p>
          <FeatureGrid features={[
            { icon: '📊', title: 'Grid overzicht', desc: 'Per medewerker per dag: beschikbaar, verlof, ziek, flex/ADV' },
            { icon: '🕐', title: 'Tijdslots', desc: 'Ochtend (08:00-12:30) en middag (12:30-17:30) apart' },
            { icon: '🏪', title: 'Showroom filter', desc: 'Filter op showroom-medewerkers voor klantafspraken' },
            { icon: '🔗', title: 'Boekingssysteem API', desc: 'Exporteer beschikbaarheidsdata als JSON voor extern systeem' },
          ]} />
          <p style={S.text}><strong>Boekingssysteem voorbereiding:</strong> Markeer medewerkers die showroombezoeken afhandelen (🏪 Showroom checkbox bij Medewerkers → Bewerken). De Showroom-badge verschijnt bij Profielen naast de andere rollen. Het systeem controleert automatisch of er voldoende capaciteit is. Bij onvoldoende bezetting worden tijdslots geblokkeerd. Medewerkers met een <strong>openstaande ziekmelding</strong> worden automatisch uitgesloten van de beschikbaarheid — ook als ze als showroom-medewerker staan gemarkeerd.</p>
          <p style={S.text}><strong>Outlook werkrooster sync:</strong> Via Instellingen → Outlook sync worden per medewerker per werkdag events aangemaakt (t/m eind volgend jaar). Events tonen korte afkortingen: VAK (vakantie), ZIEK, BV (bijzonder verlof), ADV, FLEX, RUIL — gevolgd door de verkorte naam. Onderbezettingsdagen krijgen een apart ⚠️ waarschuwingsevent. Bij rooster- of contractwijzigingen wordt Outlook automatisch bijgewerkt.</p>
          <Tip>Stel minimum bezetting in (standaard: 2). Het systeem toont per dag ✅ voldoende, ⚠️ onder minimum, of 🚫 geen capaciteit.</Tip>

          <h4 style={S.subhead}>💾 Backup & Restore</h4>
          <p style={S.text}>In het tabblad <strong>Instellingen</strong> vind je backup-opties:</p>
          <ul style={S.list}>
            <li><strong>Volledige backup</strong> — Download alle data als JSON-bestand (medewerkers, verlof, overuren, feestdagen, flex, instellingen). Bewaar deze regelmatig!</li>
            <li><strong>Template export</strong> — Download alleen de app-instellingen zónder medewerkerdata. Handig als basis voor een nieuwe installatie (white-label).</li>
            <li><strong>Backup herstellen</strong> — Upload een eerder gedownload JSON-bestand om alle data te herstellen. Let op: bestaande data met hetzelfde ID wordt overschreven.</li>
          </ul>
          <Warning>Backup herstellen overschrijft bestaande records! Maak altijd eerst een backup voordat je een restore uitvoert.</Warning>

          <h4 style={S.subhead}>🔒 Beveiliging</h4>
          <p style={S.text}>In het tabblad Instellingen zie je de beveiligingsstatus van de app:</p>
          <ul style={S.list}>
            <li><strong>Wachtwoord hashing</strong> — Alle wachtwoorden worden opgeslagen als SHA-256 hash. Bestaande accounts worden automatisch gemigreerd bij eerste inlog.</li>
            <li><strong>API keys</strong> — Overzicht van alle gebruikte API's (Firebase, Vercel, Outlook) met status. Geen enkele key heeft een vervaldatum.</li>
            <li><strong>Firebase API key</strong> — De Firebase key in de frontend is publiek en veilig; toegang wordt bepaald door Firestore Security Rules.</li>
          </ul>
          <Tip>Wijzig het standaard beheerderwachtwoord na eerste installatie. Stel ook Firestore Security Rules in via de Firebase Console voor extra beveiliging.</Tip>

          <h4 style={S.subhead}>📦 Import & Export</h4>
          <FeatureGrid features={[
            { icon: '📄', title: 'JSON Backup', desc: 'Volledige backup van alle data' },
            { icon: '📊', title: 'CSV per categorie', desc: 'Verlof, overuren of flex apart' },
            { icon: '📕', title: 'PDF per medewerker', desc: 'Individueel overzicht met saldi' },
            { icon: '📗', title: 'PDF totaaloverzicht', desc: 'Kies periode, medewerker(s) en optioneel mutatielogboek — toont saldi + alle individuele mutaties' },
            { icon: '📥', title: 'CSV importeren', desc: 'Upload template met historische data' },
          ]} />
          <p style={S.text}><strong>PDF Totaaloverzicht:</strong> Klik "📄 PDF Totaaloverzicht" bij Medewerkers. Kies een periode (van/tot datum) en selecteer alle medewerkers of een specifieke medewerker. Het overzicht toont per medewerker: contract, verlofsaldo, opgenomen (verleden), gepland (toekomst), ingediend, resterend, ziekuren (incl. open ziekmeldingen), flex/ADV, en overuren (verdiend/gebruikt/saldo).</p>
          <Tip>Vink <strong>"📋 Inclusief mutatielogboek"</strong> aan om per medewerker alle individuele verlofaanvragen, ziekmeldingen en overuren in de periode als aparte pagina's toe te voegen aan het PDF. Handig voor jaaroverzichten en audits.</Tip>

          <h4 style={S.subhead}>📅 Werkrooster & perioden</h4>
          <p style={S.text}><strong>Werkrooster:</strong> Per medewerker stel je per dag ochtend-/middag-/avondblokken in. Dit wordt gebruikt voor automatische urenberekening bij verlofaanvragen en de kalender.</p>
          <p style={S.text}><strong>Roosterwijzigingen:</strong> Voor incidentele wijzigingen (verschoven werkdag). De oorspronkelijke dag wordt <span style={{ textDecoration: 'line-through' }}>doorgestreept</span> in de kalender en de nieuwe dag blauw weergegeven.</p>
          <p style={S.text}><strong>Vakantieperioden:</strong> Bij verlofaanvragen in deze perioden ziet de medewerker een waarschuwing.</p>
          <p style={S.text}><strong>Feestdagen:</strong> Worden automatisch overgeslagen bij urenberekening en getoond in de kalender.</p>

          <Warning><strong>Let op bij verwijderen:</strong> Verwijderde medewerkers zijn permanent weg inclusief al hun verlof-, overuren- en flexdata. Gebruik "Uit dienst" als je data wilt bewaren.</Warning>
        </>
      ),
    });
  }

  // ===== GOEDKEUREN (manager) =====
  if (rol === 'manager') {
    all.push({
      key: 'goedkeuren', icon: '✅', title: 'Goedkeuren & beoordelen',
      roles: ['manager'],
      render: () => (
        <>
          <p style={S.text}>Als manager kun je verlofaanvragen goedkeuren en afkeuren. Openstaande aanvragen verschijnen direct op je Dashboard.</p>
          <MockPhone title="Dashboard — Manager">
            <MockCard title="⏳ Openstaande aanvragen (2)" color="#FFF8E1" border="1px solid #FFD54F">
              <div style={{ padding: '4px 0', borderBottom: '1px solid #edebe9' }}>
                <div style={{ fontSize: 9, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><strong>Lisa</strong> · Vakantie · 24–28 feb · 40u</span>
                  <span><MockBtn color="#107C10" small>✅</MockBtn><MockBtn color="#D13438" small>❌</MockBtn></span>
                </div>
              </div>
              <div style={{ padding: '4px 0', marginTop: 4 }}>
                <div style={{ fontSize: 9, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><strong>Erwin</strong> · BV · 10 mrt · 8u</span>
                  <span><MockBtn color="#107C10" small>✅</MockBtn><MockBtn color="#D13438" small>❌</MockBtn></span>
                </div>
              </div>
            </MockCard>
          </MockPhone>
          <Tip>De medewerker ontvangt automatisch een melding bij goed- of afkeuring. Je kunt ook verlof indienen namens medewerkers via het Dashboard snelformulier of via Indienen, met de optie <strong>✅ Direct goedkeuren</strong> om in één stap te beoordelen.</Tip>
          <Warning><strong>Manager beperkingen:</strong> Je ziet opmerkingen alleen als je de aanvraag zelf hebt ingediend. Aanvragen ouder dan 1 maand worden niet getoond (behalve openstaande en open ziekmeldingen).</Warning>
        </>
      ),
    });
  }

  // ===== FLEX/ADV BALANS (medewerker) =====
  if (rol === 'medewerker') {
    all.push({
      key: 'flexbalans', icon: '🔀', title: 'Flex/ADV balans',
      roles: ['medewerker'],
      render: () => (
        <>
          <p style={S.text}>Als flex is ingeschakeld voor jouw account, kun je <strong>Flex (plus)</strong> en <strong>Flex (min)</strong> als verloftype kiezen bij het indienen.</p>

          <h4 style={S.subhead}>Hoe werkt het?</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, margin: '12px 0' }}>
            <div style={{ background: '#00B7C310', borderRadius: 12, padding: 14, border: '1px solid #00B7C340', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>➕</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#00B7C3' }}>Flex (plus)</div>
              <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>Je werkt extra uren bovenop je contract. Balans gaat omhoog.</div>
            </div>
            <div style={{ background: '#E86E1C10', borderRadius: 12, padding: 14, border: '1px solid #E86E1C40', textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>➖</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#E86E1C' }}>Flex (min)</div>
              <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>Je neemt flex-verlof op. Balans gaat omlaag.</div>
            </div>
          </div>

          <h4 style={S.subhead}>Regels & limieten</h4>
          <ul style={S.list}>
            <li><strong>Max 8 uur per week</strong> — Je kunt maximaal 8 uur flex-min per kalenderweek opnemen</li>
            <li><strong>Maandbalans</strong> — Binnen een kalendermaand moeten je plus- en minuren weer in balans zijn (netto ≈ 0)</li>
            <li><strong>Geen sparen & opmaken</strong> — Je kunt niet de hele maand plus-uren sparen om ze in de laatste week allemaal als verlof op te nemen</li>
            <li><strong>Weekbudget</strong> — De beheerder stelt je wekelijks flex-budget in (bijv. 4u/week)</li>
          </ul>

          <Tip>Bij het indienen van Flex (plus) of Flex (min) zie je in het formulier automatisch hoeveel flex-uren je deze week en maand nog beschikbaar hebt.</Tip>

          <p style={S.text}>Op je Dashboard staat een <strong>Flex/ADV overzicht</strong> kaart met je weekbudget, plus- en minuren, jaarbalans, weeklimieten, maandbalans en een log van al je recente flex boekingen.</p>
        </>
      ),
    });
  }

  // ===== PROFIELEN =====
  all.push({
    key: 'profielen', icon: '👤', title: 'Profielen & rollen',
    roles: ['medewerker', 'manager', 'admin', 'flexkracht'],
    render: () => (
      <>
        <p style={S.text}>Het systeem kent vier profielen. Sommige medewerkers hebben meerdere profielen en kunnen wisselen via de profiel-kiezer in de header.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, margin: '14px 0' }}>
          {[
            { icon: '👑', name: 'Beheerder', color: '#8B5CF6', perms: ['Alle instellingen', 'Medewerkers beheren', 'Verlof goedkeuren', 'Saldi aanpassen', 'Import/export'] },
            { icon: '📋', name: 'Manager', color: '#E86E1C', perms: ['Verlof goedkeuren', 'Indienen namens team', 'Flex inplannen', 'Overzicht bekijken'] },
            { icon: '👤', name: 'Medewerker', color: '#0078D4', perms: ['Eigen verlof aanvragen', 'Overuren registreren', 'Kalender bekijken', 'Eigen saldo inzien'] },
            { icon: '⚡', name: 'Flexkracht', color: '#FFB900', perms: ['Eigen flex dashboard met persoonlijk saldo', 'Planning bekijken', 'Uren invoeren (inklokken/uitklokken)', 'Kalender bekijken'] },
          ].map((r, i) => (
            <div key={i} style={{ background: `${r.color}08`, borderRadius: 12, padding: 14, border: `1px solid ${r.color}30` }}>
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 28 }}>{r.icon}</span>
                <div style={{ fontSize: 13, fontWeight: 700, color: r.color, marginTop: 4 }}>{r.name}</div>
              </div>
              <ul style={{ fontSize: 10, color: '#605e5c', lineHeight: 1.8, paddingLeft: 16, margin: 0 }}>
                {r.perms.map((p, j) => <li key={j}>{p}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <Tip>Als je meerdere profielen hebt (bijv. Medewerker + Manager), verschijnt er een <strong>profiel-kiezer</strong> in de header. Tik erop om te wisselen. Elk profiel toont andere menu-items en functionaliteit.</Tip>
      </>
    ),
  });

  // ===== NAVIGATIE =====
  all.push({
    key: 'navigatie', icon: '🧭', title: 'Navigatie & menu',
    roles: ['medewerker', 'manager', 'admin', 'flexkracht'],
    render: () => {
      const menuItems = {
        admin: [
          { icon: '📊', label: 'Dashboard', desc: 'Startpagina met saldi en openstaande aanvragen' },
          { icon: '📝', label: 'Indienen', desc: 'Verlof, ziekmelding of overuren indienen' },
          { icon: '📋', label: 'Verlofoverzicht', desc: 'Alle aanvragen bekijken en beoordelen' },
          { icon: '👤', label: 'Verlof medewerkers', desc: 'Per medewerker saldo en verlof beheren' },
          { icon: '📅', label: 'Kalender', desc: 'Teamkalender met alle verlof en feestdagen' },
          { icon: '⚡', label: 'Flexkrachten', desc: 'Inplannen en uren bekijken' },
          { icon: '⚙️', label: 'Beheer', desc: 'Alle instellingen en administratie' },
          { icon: '📖', label: 'Handleiding', desc: 'Deze pagina' },
        ],
        manager: [
          { icon: '📊', label: 'Dashboard', desc: 'Saldi, openstaande aanvragen, snel indienen' },
          { icon: '📝', label: 'Indienen', desc: 'Verlof, ziekmelding of overuren' },
          { icon: '📋', label: 'Overzicht', desc: 'Alle verlofaanvragen beoordelen' },
          { icon: '👤', label: 'Verlof medewerkers', desc: 'Per medewerker bekijken' },
          { icon: '📅', label: 'Kalender', desc: 'Teamkalender' },
          { icon: '⚡', label: 'Flexkrachten', desc: 'Inplannen en beheren' },
          { icon: '📖', label: 'Handleiding', desc: 'Deze pagina' },
        ],
        medewerker: [
          { icon: '📊', label: 'Dashboard', desc: 'Saldi en recente aanvragen' },
          { icon: '📝', label: 'Indienen', desc: 'Verlof, ziekmelding of overuren' },
          { icon: '📋', label: 'Overzicht', desc: 'Je eigen verlofaanvragen' },
          { icon: '📅', label: 'Kalender', desc: 'Teamkalender' },
          { icon: '📖', label: 'Handleiding', desc: 'Deze pagina' },
        ],
        flexkracht: [
          { icon: '📊', label: 'Dashboard', desc: 'Startpagina' },
          { icon: '📅', label: 'Kalender', desc: 'Teamkalender' },
          { icon: '⚡', label: 'Mijn Flex', desc: 'Diensten, uren en saldi' },
          { icon: '📖', label: 'Handleiding', desc: 'Deze pagina' },
        ],
      };

      const items = menuItems[rol] || menuItems.medewerker;

      return (
        <>
          <p style={S.text}>Je menu-items hangen af van je profiel. Hier zie je welke pagina's beschikbaar zijn voor jou:</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '12px 0' }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', background: '#faf9f8', borderRadius: 10, border: '1px solid #edebe9' }}>
                <span style={{ fontSize: 22 }}>{item.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: '#605e5c' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <h4 style={S.subhead}>📱 Mobiel</h4>
          <p style={S.text}>Op je telefoon staan de eerste 4 items als tabbladen onderin het scherm. De rest vind je via het <strong>☰ menu</strong> rechtsboven.</p>

          <MockPhone title="Mobiel menu">
            <div style={{ padding: 4, fontSize: 9, color: '#605e5c', textAlign: 'center' }}>...pagina-inhoud...</div>
            <div style={{ display: 'flex', borderTop: '1px solid #edebe9', background: '#faf9f8' }}>
              {items.slice(0, 4).map((item, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center', padding: '6px 2px', color: i === 0 ? '#E86E1C' : '#a19f9d', fontSize: 7, fontWeight: i === 0 ? 700 : 400 }}>
                  <div style={{ fontSize: 14 }}>{item.icon}</div>
                  {item.label}
                </div>
              ))}
              {items.length > 4 && (
                <div style={{ flex: 1, textAlign: 'center', padding: '6px 2px', color: '#a19f9d', fontSize: 7 }}>
                  <div style={{ fontSize: 14 }}>☰</div>
                  Meer
                </div>
              )}
            </div>
          </MockPhone>

          <Tip>De beheerder kan menu-items hernoemen en de volgorde aanpassen via <strong>Beheer → Menu aanpassen</strong>.</Tip>
        </>
      );
    },
  });

  return all.filter(s => s.roles.includes(rol || 'medewerker'));
};


/* ============================================================
   MAIN COMPONENT
   ============================================================ */

function UserGuide() {
  const { effectiefRol, profielLabel } = useApp();
  const rol = effectiefRol || 'medewerker';
  const sections = getSections(rol);
  const [activeIdx, setActiveIdx] = useState(0);

  const rolTitles = { admin: 'Beheerder', manager: 'Manager', medewerker: 'Medewerker', flexkracht: 'Flexkracht' };
  const rolColors = { admin: '#8B5CF6', manager: '#E86E1C', medewerker: '#0078D4', flexkracht: '#FFB900' };
  const rc = rolColors[rol];
  const activeSection = sections[activeIdx] || sections[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, maxWidth: 900 }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', borderRadius: '16px 16px 0 0', padding: '28px 32px', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <span style={{ fontSize: 36 }}>📖</span>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>Handleiding</h2>
            <p style={{ fontSize: 13, opacity: 0.7 }}>{profielLabel || 'Slaaploods Verlofbeheer'}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ background: rc, padding: '3px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>{rolTitles[rol]}</span>
          <span style={{ fontSize: 12, opacity: 0.5 }}>{sections.length} onderwerpen op maat voor jouw profiel</span>
        </div>
      </div>

      {/* Navigation pills */}
      <div style={{ background: '#fff', borderBottom: '1px solid #edebe9', padding: '12px 16px', overflowX: 'auto', display: 'flex', gap: 6 }} className="r-table-wrap">
        {sections.map((s, i) => (
          <button key={s.key} onClick={() => setActiveIdx(i)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            borderRadius: 20, border: activeIdx === i ? `2px solid ${rc}` : '1px solid #edebe9',
            background: activeIdx === i ? `${rc}10` : '#faf9f8',
            cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12, fontWeight: activeIdx === i ? 700 : 500,
            color: activeIdx === i ? rc : '#605e5c', transition: 'all 0.15s',
          }}>
            <span style={{ fontSize: 16 }}>{s.icon}</span>
            {s.title}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ background: '#fff', borderRadius: '0 0 16px 16px', padding: '28px 32px', minHeight: 400, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} className="r-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: `2px solid ${rc}25` }}>
          <span style={{ fontSize: 32 }}>{activeSection.icon}</span>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>{activeSection.title}</h3>
        </div>

        {activeSection.render()}

        {/* Prev / Next */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 36, paddingTop: 20, borderTop: '1px solid #f3f2f1' }}>
          <button onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))} disabled={activeIdx === 0}
            style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #edebe9', background: '#faf9f8', cursor: activeIdx === 0 ? 'default' : 'pointer', opacity: activeIdx === 0 ? 0.3 : 1, fontSize: 13, fontWeight: 600, color: '#605e5c' }}>
            ← Vorige
          </button>
          <span style={{ fontSize: 12, color: '#a19f9d', alignSelf: 'center' }}>{activeIdx + 1} / {sections.length}</span>
          <button onClick={() => setActiveIdx(Math.min(sections.length - 1, activeIdx + 1))} disabled={activeIdx === sections.length - 1}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: activeIdx === sections.length - 1 ? '#edebe9' : rc, color: '#fff', cursor: activeIdx === sections.length - 1 ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            Volgende →
          </button>
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: 20, color: '#a19f9d', fontSize: 11 }}>
        Slaaploods Verlofbeheer v3.0 — Bij vragen neem contact op met de beheerder
      </div>
    </div>
  );
}


/* Shared styles */
const S = {
  text: { fontSize: 14, lineHeight: 1.7, color: '#323130', marginBottom: 12 },
  subhead: { fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginTop: 24, marginBottom: 8 },
  list: { fontSize: 13, lineHeight: 1.8, color: '#605e5c', paddingLeft: 20, marginTop: 4 },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, marginTop: 16 },
};

export default UserGuide;
